import GlitchHover from "./glitch-hover";

export default class GlitchHoverItem {

  public static SPEED: number = 0.001;

  private readonly glitchHover: GlitchHover;

  public readonly img: HTMLImageElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;

  private readonly dpr: number = 1;

  private imageX: number = 0;
  private imageScaledNaturalWidthLpx: number = 0;
  private imageScaledNaturalHeightLpx: number = 0;

  private currentY: number = 0;
  private aimY: number = 0;

  private readonly mousemoveHandler: EventListener;
  private readonly mouseenterHandler: EventListener;
  private readonly mouseleaveHandler: EventListener;
  private readonly loadHandler: EventListener;

  private hadFirstDraw: boolean = false;

  constructor(img: HTMLImageElement, glitchHover: GlitchHover)
  {
    this.glitchHover = glitchHover;

    this.img = img;
    this.dpr = this.glitchHover.dpr;

    this.canvas = document.createElement('canvas');
    this.canvas.setAttribute('aria-hidden', 'true');
    this.canvas.style.setProperty('position', 'absolute');
    this.canvas.style.setProperty('top', '0');
    this.canvas.style.setProperty('left', '0');

    const context = this.canvas.getContext('2d', {alpha: true});
    if(context instanceof CanvasRenderingContext2D) {
      this.context = context;
      this.context.scale(this.dpr, this.dpr);
    } else {
      throw new Error("Context not found");
    }

    this.img.parentElement?.insertAdjacentElement('afterend', this.canvas);

    this.mousemoveHandler = this.handleMouseMoveEvent.bind(this);
    this.mouseenterHandler = this.handleMouseEnterEvent.bind(this);
    this.mouseleaveHandler = this.handleMouseLeaveEvent.bind(this);
    this.loadHandler = this.handleLoadEvent.bind(this);
    this.setActive(false);

    if(this.img.complete) {
      this.enable();
    } else {
      this.img.addEventListener('load', this.loadHandler, false);
    }
  }

  private enable(): void
  {
    this.updateCanvasSize();
    this.canvas.addEventListener('mouseenter', this.mouseenterHandler, false);
    this.canvas.addEventListener('mouseleave', this.mouseleaveHandler, false);
    this.canvas.addEventListener('mousemove', this.mousemoveHandler, false);
  }

  public updateCanvasSize(): void
  {
    if(this.img.complete === false) {
      return;
    }

    const naturalWidth = this.img.naturalWidth;
    const naturalHeight = this.img.naturalHeight;
    const naturalRatio = naturalWidth / naturalHeight;

    const visibleWidth = this.img.width;
    const visibleHeight = this.img.height;
    const visibleRatio = visibleWidth / visibleHeight;

    //get correct size when object-fit: "cover"
    if(naturalRatio < visibleRatio) {

      //vertical overflow
      this.imageScaledNaturalWidthLpx = visibleWidth;
      this.imageScaledNaturalHeightLpx = visibleWidth / naturalRatio;
      this.imageX = 0;

    } else {

      //horizontal overflow 
      this.imageScaledNaturalWidthLpx = visibleHeight * naturalRatio;
      this.imageScaledNaturalHeightLpx = visibleHeight;
      this.imageX = (visibleWidth - this.imageScaledNaturalWidthLpx) / 2;
    }

    this.canvas.width = this.img.width * this.dpr;
    this.canvas.height = this.img.height * this.dpr;
  }

  private handleLoadEvent(event: Event): void
  {
    this.enable();
  }

  public handleMouseEnterEvent(event: Event): void
  {
    if(event instanceof MouseEvent === false) {
      return;
    }

    const rect = this.canvas.getBoundingClientRect();
    var y = Math.round(event.clientY - rect.top);  //y position within the element.

    this.currentY = y;
    this.aimY = y;

    this.setActive(true);
    this.draw();
  }

  private handleMouseMoveEvent(event: Event): void
  {
    if(event instanceof MouseEvent === false) {
      return;
    }

    const rect = this.canvas.getBoundingClientRect();
    var y = Math.round(event.clientY - rect.top);  //y position within the element.

    this.aimY = y;
  }

  public handleMouseLeaveEvent(event: Event): void
  {
    if(event instanceof MouseEvent === false) {
      return;
    }
    this.setActive(false);
  }

  public isActive(): boolean
  {
    const isActive = (this == this.glitchHover.getActiveHoverItem());
    return isActive;
  }

  public setActive(active: boolean = true): void
  {
    this.hadFirstDraw = false;
    if(active) {
      this.canvas.style.setProperty('opacity', '1');
      this.glitchHover.setActiveHoverItem(this);
    } else if (this.isActive()) {
      this.canvas.style.removeProperty('opacity');
      this.glitchHover.setActiveHoverItem(null);
    }
  }

  private draw(): void
  {
    if(!this.isActive()) {
      return;
    }

    requestAnimationFrame(() => {
      this.draw();
    })

    //get slice Y position
    const aimDifference = this.aimY - this.currentY;
    const aimDifferenceMod = aimDifference * GlitchHoverItem.SPEED;
    const aimModded = this.currentY + aimDifferenceMod;
    // round to two decimal places
    const newCurrentY = Math.round(aimModded * 100) / 100;

    if(this.hadFirstDraw === false || (this.img.complete && this.currentY != newCurrentY)) { //draw only if needed

      this.hadFirstDraw = true;

      this.currentY = newCurrentY;

      //get slice image data...
      //draw background for transparent images
      this.glitchHover.sliceContext.beginPath();
      this.glitchHover.sliceContext.fillStyle = GlitchHover.DEFAULT_BG;
      this.glitchHover.sliceContext.fillRect(0,0,this.imageScaledNaturalWidthLpx, GlitchHover.SLICE_HEIGHT_PX);
      this.glitchHover.sliceContext.closePath();

      //get the slice
      this.glitchHover.sliceContext.drawImage(
        this.img,
        this.imageX,
        (-1 * this.currentY),
        this.imageScaledNaturalWidthLpx,
        this.imageScaledNaturalHeightLpx
      );

      //repeat the slice
      const pattern = this.context.createPattern(
        this.glitchHover.sliceCanvas,
        'repeat-y'
      ) as CanvasPattern; //null is returned when source is not loaded = not possible when using canvas

      // draw bottom half with pattern
      this.context.beginPath();
      this.context.fillStyle = pattern;
      this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
      this.context.closePath();
    }    
  }
}